import { useCallback, useEffect, useRef } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

// Helpers
import api from 'frontend/js/api';
import shoppingBasket from '@src/utils/shopping-basket';

// Stores
import { useCartLoadingStore } from '@src/stores/cartLoadingStore';

// Types
import { ICart, ICartItemProperties, IAddedCartItems, ICartAttributes } from '@src/types/cart';
import helpers from 'frontend/js/helpers';

interface AddToCartMutationArgs {
  variantId: string;
  quantity: number | string;
  properties?: ICartItemProperties;
}

interface AddToCartRequestBody {
  items: AddToCartRequestBodyItem[];
}

interface AddToCartRequestBodyItem {
  id: string;
  quantity: number | string;
  properties?: ICartItemProperties;
}

interface UpdateItemMutationArgs {
  key: string;
  quantity: number;
  properties?: ICartItemProperties;
}

interface UpdateItemRequestBody {
  id: string;
  quantity: number;
  properties?: ICartItemProperties;
}

interface UpdateCartMutationArgs {
  note?: string;
  updates?: number[];
  attributes?: { [key: string]: string };
}

interface UpdateCartRequestBody {
  note?: string;
  updates?: number[];
  attributes?: { [key: string]: string };
}

const fetchCartTokenQuery = async (url: string): Promise<string> => {
  const res = await fetch(url);

  const jsonData = await res.json();

  if (!res.ok) new Error('Filed to fetch cart token');

  return jsonData.token;
};

const fetchCartQuery = async (url: string): Promise<ICart> => {
  const res = await fetch(url);

  const jsonData = await res.json();

  if (!res.ok) new Error('Filed to fetch cart');

  return jsonData;
};

export const addToCartMutation = async (
  url: string,
  { arg }: { arg: AddToCartMutationArgs[] }
): Promise<IAddedCartItems> => {
  const itemsData: AddToCartRequestBodyItem[] = arg.map((item) => {
    const { variantId, quantity, properties } = item;

    const dataItem: AddToCartRequestBodyItem = {
      id: variantId,
      quantity
    };

    if (properties) {
      dataItem.properties = properties;
    }

    return dataItem;
  });

  const data: AddToCartRequestBody = {
    items: itemsData
  };

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...api.getXhrRequestConfig().headers
    },
    body: JSON.stringify(data)
  });

  const jsonData = await res.json();

  if (!res.ok) {
    const error = new Error(jsonData.message);
    alert(error.message);

    throw error;
  }

  return jsonData;
};

const updateItemMutation = async (url: string, { arg }: { arg: UpdateItemMutationArgs }) => {
  const { key, quantity, properties } = arg;

  const data: UpdateItemRequestBody = {
    id: key,
    quantity
  };

  if (properties) {
    data.properties = properties;
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...api.getXhrRequestConfig().headers
    },
    body: JSON.stringify(data)
  });

  const jsonData = await res.json();

  if (!res.ok) {
    const error = new Error(jsonData.message);

    throw error;
  }

  return jsonData;
};

const updateCartMutation = async (url: string, { arg }: { arg: UpdateCartMutationArgs }) => {
  const { note = '', attributes, updates } = arg;

  const data: UpdateCartRequestBody = {
    note
  };

  if (updates) {
    data.updates = updates;
  }

  if (attributes) {
    data.attributes = attributes;
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...api.getXhrRequestConfig().headers
    },
    body: JSON.stringify(data)
  });

  const jsonData = await res.json();

  if (!res.ok) {
    const error = new Error(jsonData.message);

    throw error;
  }

  return jsonData;
};

const basketDataMutation = async (url: string, { arg }: { arg: { cartToken: string; cartData: ICart } }) => {
  const res = await shoppingBasket.fetchShoppingBasket(arg);
  const { success, cart } = res;

  if (!success) {
    throw new Error('Failed to fetch shopping basket data');
  }

  return cart;
};

// Global listener management
let listenersRegistered = false;
let isEventProcessing = false;

function useShoppingBasketCart() {
  const startLoading = useCartLoadingStore((state) => state.startLoading);
  const stopLoading = useCartLoadingStore((state) => state.stopLoading);
  const isGlobalCartLoading = useCartLoadingStore((state) => state.isGlobalCartLoading);

  // Fetch cart token
  const { data: cartToken, mutate: refreshCartToken } = useSWR('/cart.js', fetchCartTokenQuery, {
    revalidateIfStale: false,
    revalidateOnFocus: false
  });

  // Fetch cart
  const {
    data: cartData,
    isLoading: isCartDataLoading,
    isValidating: isCartRefreshing,
    mutate: refreshCart
  } = useSWR('/cart?view=data', fetchCartQuery, { revalidateIfStale: false, revalidateOnFocus: false });

  // Add to cart
  const {
    data: addToCartData,
    trigger: addToCartTrigger,
    isMutating: isAddingToCart,
    error: addToCartError
  } = useSWRMutation('/cart/add.js', addToCartMutation);

  // Update item
  const {
    trigger: updateItemTrigger,
    isMutating: isUpdatingItem,
    error: updateItemError
  } = useSWRMutation('/cart/change.js', updateItemMutation);

  // Update cart
  const {
    trigger: updateCartTrigger,
    isMutating: isUpdatingCart,
    error: updateCartError
  } = useSWRMutation('/cart/update.js', updateCartMutation);

  // Basket data
  const {
    trigger: triggerBasketData,
    isMutating: isBasketDataLoading,
    error: basketDataError
  } = useSWRMutation('basket-data', basketDataMutation);

  // Utility functions to add/update items and refresh cart
  const addToCartAndFetch = async (arg) => {
    await addToCartTrigger(arg);

    refreshCart();
  };

  const updateItemAndFetch = async (arg, processBasketData = false) => {
    await updateItemTrigger(arg);

    const currentCartData = await refreshCart();

    if (processBasketData) {
      await handleBasketData(currentCartData as ICart);
    }
  };

  const updateCartAndFetch = async (arg) => {
    await updateCartTrigger(arg);

    refreshCart();
  };

  const handleBasketData = useCallback(
    async (fetchedCartData?: ICart) => {
      startLoading('basketData');

      let currentCartData = fetchedCartData;
      let currentCartToken = cartToken;

      if (!currentCartData) {
        currentCartData = await refreshCart();
      }

      if (!currentCartToken) {
        currentCartToken = await refreshCartToken();
      }

      if (!currentCartData || (currentCartData && currentCartData.items.length === 0)) {
        stopLoading('basketData');
        return;
      }

      try {
        const basketResult = await triggerBasketData({
          cartToken: currentCartToken as string,
          cartData: currentCartData as ICart
        });

        const basketItems = basketResult?.cart?.line_items?.line_items || [];
        if (!basketItems) {
          throw new Error('No items found in the shopping basket');
        }

        let quantities: number[] = [];
        let isCartUpdated = false;
        let updateRequestBody: { updates?: number[]; attributes?: ICartAttributes } = {};
        let addItems: { variantId: string; quantity: string; properties: ICartItemProperties }[] = [];
        let processedSkus: string[] = [];

        // Compare cart items with the Shopping basket items and if there are any
        // changes/discounts, then remove all products (except GWP) and
        // re-add based on basket items.
        for (const cartItem of currentCartData.items) {
          if (cartItem.is_gwp_product || cartItem.sku == 'GCE') {
            quantities.push(cartItem.quantity);
            continue;
          }

          quantities.push(0);

          if (processedSkus.includes(cartItem.sku)) continue;
          processedSkus.push(cartItem.sku);

          const skuBasketItems = basketItems.filter((item) => item.sku == cartItem.sku);

          for (const basketItem of skuBasketItems) {
            const properties = {};

            if (
              (basketItem.new_price !== null &&
                basketItem.new_price !== '' &&
                basketItem.new_price != (cartItem.original_price * 0.01).toFixed(2)) ||
              basketItem.new_price != (cartItem.final_price * 0.01).toFixed(2)
            ) {
              properties['_new_price'] = (parseFloat(basketItem.new_price) * 100).toFixed(0);
              properties['_discount_message'] = basketItem.message;

              isCartUpdated = true;
            }

            if (basketItem.discount_applied_number) {
              properties['_discount_applied_number'] = basketItem.discount_applied_number;
              isCartUpdated = true;
            }

            addItems.unshift({
              variantId: '' + cartItem.id,
              quantity: basketItem.quantity,
              properties: properties
            });
          }
        }

        const isResetRequired =
          (isCartUpdated || currentCartData.items.length != basketItems.length) && addItems.length;

        if (isResetRequired) {
          updateRequestBody.updates = quantities;
        }

        // Applied voucher and gift card data is saved to cart attributes
        const appliedVouchers = basketResult?.cart?.discounts_applied?.discounts_applied || [];
        const appliedGiftcards = basketResult?.cart?.giftcards_applied?.giftcards_applied || [];
        const newGiftCardDiscount = basketResult?.cart?.new_discount_or_gc || [];

        if (!helpers.isEqual(appliedVouchers, currentCartData.applied_discounts)) {
          if (!updateRequestBody.attributes) updateRequestBody.attributes = {};
          updateRequestBody.attributes['discounts'] = JSON.stringify(appliedVouchers);
        }

        if (!helpers.isEqual(appliedGiftcards, currentCartData.applied_giftcards)) {
          if (!updateRequestBody.attributes) updateRequestBody.attributes = {};
          if (newGiftCardDiscount && newGiftCardDiscount.status_code !== 'INVALID') {
            updateRequestBody.attributes['giftcards'] = JSON.stringify(appliedGiftcards);
          }
        }

        if (Object.keys(updateRequestBody).length) {
          await updateCartTrigger(updateRequestBody);
        }

        if (isResetRequired) {
          await addToCartAndFetch(addItems);
        }
      } catch (error) {
        console.error('Error in handleBasketData:', error);
      } finally {
        stopLoading('basketData');
      }
    },
    [cartToken, startLoading, stopLoading]
  );

  // Store current functions in refs for event handlers
  const refreshCartRef = useRef(refreshCart);
  const handleBasketDataRef = useRef(handleBasketData);
  const triggerBasketDataRef = useRef(triggerBasketData);

  refreshCartRef.current = refreshCart;
  handleBasketDataRef.current = handleBasketData;
  triggerBasketDataRef.current = triggerBasketData;

  // Register global event listeners once
  useEffect(() => {
    if (listenersRegistered) return;

    listenersRegistered = true;

    const handleCartItemAdded = async () => {
      // Prevent multiple simultaneous executions
      if (isEventProcessing) return;

      isEventProcessing = true;

      try {
        // Force refresh and get the latest cart data
        const currentCartData = await refreshCartRef.current();

        await handleBasketDataRef.current(currentCartData as ICart);
      } finally {
        isEventProcessing = false;
      }
    };

    const handleCartItemAddedAndOpen = async () => {
      // Prevent multiple simultaneous executions
      if (isEventProcessing) return;

      isEventProcessing = true;

      try {
        // Force refresh and get the latest cart data
        const currentCartData = await refreshCartRef.current();

        // Open cart after refresh is complete
        const isCartPage = window.location.pathname.includes('/cart');
        if (!isCartPage) {
          window.dispatchEvent(new CustomEvent('cart:open'));
        }

        await handleBasketDataRef.current(currentCartData as ICart);
      } finally {
        isEventProcessing = false;
      }
    };

    window.addEventListener('cart:item_added', handleCartItemAdded);
    window.addEventListener('cart:item_added_and_open', handleCartItemAddedAndOpen);

    return () => {
      window.removeEventListener('cart:item_added', handleCartItemAdded);
      window.removeEventListener('cart:item_added_and_open', handleCartItemAddedAndOpen);
      listenersRegistered = false;
    };
  }, []);

  // Update global loading state using reference counting
  // Use this state to block UI interactions while cart operations are in progress
  useEffect(() => {
    if (isCartDataLoading || isCartRefreshing) {
      startLoading('cartData');
    } else {
      stopLoading('cartData');
    }
  }, [isCartDataLoading, isCartRefreshing, startLoading, stopLoading]);

  useEffect(() => {
    if (isAddingToCart) {
      startLoading('addingToCart');
    } else {
      stopLoading('addingToCart');
    }
  }, [isAddingToCart, startLoading, stopLoading]);

  useEffect(() => {
    if (isUpdatingItem) {
      startLoading('updatingItem');
    } else {
      stopLoading('updatingItem');
    }
  }, [isUpdatingItem, startLoading, stopLoading]);

  useEffect(() => {
    if (isUpdatingCart) {
      startLoading('updatingCart');
    } else {
      stopLoading('updatingCart');
    }
  }, [isUpdatingCart, startLoading, stopLoading]);

  useEffect(() => {
    if (isBasketDataLoading) {
      startLoading('basketDataMutation');
    } else {
      stopLoading('basketDataMutation');
    }
  }, [isBasketDataLoading, startLoading, stopLoading]);

  return {
    cartData,
    isCartDataLoading: isCartDataLoading || isCartRefreshing,

    addToCartData,
    isAddingToCart,
    addToCartError,

    isUpdatingItem,
    updateItemError,

    isUpdatingCart,
    updateCartError,

    addToCartAndFetch,
    updateItemAndFetch,
    updateCartAndFetch,

    isBasketDataLoading: isBasketDataLoading,
    basketDataError,

    // Global loading state from zustand store
    isGlobalCartLoading
  };
}

export default useShoppingBasketCart;
